import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const emptyAddress = { street: "", city: "", state: "", zip: "" };

export const composeAddress = (p) => {
  if (!p) return "";
  const line1 = (p.street || "").trim();
  const cityState = [p.city, p.state].filter(Boolean).join(", ");
  const tail = [cityState, (p.zip || "").trim()].filter(Boolean).join(" ").trim();
  return [line1, tail].filter(Boolean).join(", ").trim();
};

// Multi-box US address entry: street + ZIP (auto-fills city/state) + city + state.
export function AddressFields({ value, onChange, idPrefix = "address", autoFocus = false }) {
  const v = { ...emptyAddress, ...(value || {}) };
  const [looking, setLooking] = useState(false);
  const set = (k, val) => onChange({ ...v, [k]: val });

  const lookupZip = async (zip) => {
    const z = (zip || "").trim();
    if (!/^\d{5}$/.test(z)) return;
    setLooking(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${z}`);
      if (!res.ok) { toast.error("ZIP code not found"); return; }
      const data = await res.json();
      const place = data.places?.[0];
      if (place) onChange({ ...v, zip: z, city: place["place name"], state: place["state abbreviation"] });
    } catch (e) { /* network — leave city/state for manual entry */ }
    finally { setLooking(false); }
  };

  return (
    <div className="space-y-2">
      <div>
        <Label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Street address</Label>
        <Input data-testid={`${idPrefix}-street-input`} autoFocus={autoFocus} value={v.street} onChange={(e) => set("street", e.target.value)} placeholder="123 Main St" className="rounded-sm mt-1" />
      </div>
      <div className="grid grid-cols-[100px_1fr_72px] gap-2">
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">ZIP</Label>
          <Input
            data-testid={`${idPrefix}-zip-input`}
            value={v.zip}
            inputMode="numeric"
            maxLength={5}
            onChange={(e) => set("zip", e.target.value.replace(/[^\d]/g, ""))}
            onBlur={(e) => lookupZip(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupZip(e.target.value); } }}
            placeholder="00000"
            className="rounded-sm mt-1 font-mono"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">City {looking && <span className="normal-case font-normal text-primary">· looking up…</span>}</Label>
          <Input data-testid={`${idPrefix}-city-input`} value={v.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className="rounded-sm mt-1" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">State</Label>
          <Input data-testid={`${idPrefix}-state-input`} value={v.state} maxLength={2} onChange={(e) => set("state", e.target.value.toUpperCase())} placeholder="ST" className="rounded-sm mt-1 uppercase" />
        </div>
      </div>
    </div>
  );
}
